// LiveRace — the shared race screen every joined participant lands on when
// the organizer taps "Start race". It's:
//
//   1. A synchronised 3-2-1 countdown (driven by event.race_started_at so
//      all clients hit "GO" at the same wall-clock moment).
//   2. GPS-tracked live stats: distance, elapsed, pace.
//   3. A live map (path + current position). Shown for both virtual and
//      physical events.
//   4. A live leaderboard powered by a Supabase Realtime broadcast channel
//      `race:{eventId}` — every runner emits their progress every 3 s and
//      listens for everyone else's. Two tabs: All / Friends.
//
// When the local runner hits Finish (or auto-finishes on target distance /
// finish geofence — see Auto-end task) we call rpc_finish_race and navigate
// to the pending-verification screen.

import { useNavigate, useParams } from 'react-router-dom';
import { useState, useEffect, useMemo, useRef } from 'react';
import { MapView } from '../../components/common/MapView';
import { Button } from '../../components/ui/button';
import { Square, Wifi, WifiOff, Loader2, Users, User, Flag, X } from 'lucide-react';
import { formatDuration } from '../../lib/formatters';
import { api, supabase } from '../../lib/supabase';
import { useApi } from '../../lib/use-api';
import { useAuth } from '../../lib/auth-context';
import { useGpsTracker } from '../../lib/use-gps';
import { useCheckpointTracker } from '../../lib/use-checkpoints';
import { playCheckpointChime, playFinishChime, vibratePattern } from '../../lib/feedback';
import { useUnits, kmToDisplay, unitLabel, paceSecPerUnit } from '../../lib/units';
import { toast } from 'sonner';

// How often each client publishes its progress on the shared channel.
const BROADCAST_INTERVAL_MS = 3000;
// The 3-2-1 window (ms) after race_started_at before the run "goes live".
const COUNTDOWN_MS = 3000;
// Pass-radius for checkpoints and the finish geofence, in metres.
const CHECKPOINT_RADIUS_M = 3;
const FINISH_RADIUS_M = 3;
// Grace window for the user to cancel an auto-finish (ms).
const AUTO_FINISH_GRACE_MS = 3000;

// Haversine helper — shared with GPS distance code but inlined here to
// avoid pulling in the whole tracker module for one call.
function metersBetween(a, b) {
  if (!a || !b) return Infinity;
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export default function MobileLiveRace() {
  const { id } = useParams();
  const nav = useNavigate();
  const { units } = useUnits();
  const { user, profile } = useAuth();
  const { data: event } = useApi(api.getEvent, [id]);
  const { data: myParticipation } = useApi(api.getMyParticipation, [id]);
  const { data: friends } = useApi(api.getFriends);

  // ---- Synchronised countdown -----------------------------------------
  const raceStartMs = event?.raceStartedAt ? new Date(event.raceStartedAt).getTime() : null;
  const goAtMs = raceStartMs ? raceStartMs + COUNTDOWN_MS : null;
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNowMs(Date.now()), 250);
    return () => clearInterval(iv);
  }, []);
  const msUntilGo = goAtMs ? goAtMs - nowMs : null;
  const showCountdown = msUntilGo != null && msUntilGo > 0;
  const countdown = showCountdown ? Math.ceil(msUntilGo / 1000) : 0;

  // Race is officially LIVE from `goAtMs` onwards for the runner.
  const running = !!goAtMs && nowMs >= goAtMs;
  const elapsed = running ? Math.floor((nowMs - goAtMs) / 1000) : 0;

  // ---- GPS ------------------------------------------------------------
  const gps = useGpsTracker({ active: running });
  const distanceKm = gps.distanceMeters / 1000;
  const targetKm = Number(event?.distanceKm || 0);
  const coverage = targetKm > 0 ? Math.min(100, Math.round((distanceKm / targetKm) * 100)) : null;

  // Current runner position (latest GPS fix).
  const currentPos = gps.path.length ? gps.path[gps.path.length - 1] : null;

  // ---- Checkpoints -----------------------------------------------------
  const eventCheckpoints = useMemo(
    () => (Array.isArray(event?.checkpoints) ? event.checkpoints : []),
    [event?.checkpoints]
  );
  const { passedIndices } = useCheckpointTracker({
    checkpoints: eventCheckpoints,
    currentPosition: running ? currentPos : null,
    radius: CHECKPOINT_RADIUS_M,
    onPass: (cp, i) => {
      playCheckpointChime();
      vibratePattern([80, 40, 80]);
      toast.success(`Checkpoint ${i + 1}${cp?.name ? ` · ${cp.name}` : ''} passed`, { duration: 1800 });
    },
  });

  const paceKmSec = distanceKm >= 0.05 && elapsed > 0 ? Math.floor(elapsed / distanceKm) : null;
  const paceUnitSec = paceKmSec && paceKmSec <= 25 * 60 ? Math.round(paceSecPerUnit(paceKmSec, units)) : null;
  const paceLabel = paceUnitSec
    ? `${Math.floor(paceUnitSec / 60)}:${String(paceUnitSec % 60).padStart(2, '0')}`
    : '--:--';
  const distanceDisplay = kmToDisplay(distanceKm, units).toFixed(2);

  // ---- Realtime leaderboard ------------------------------------------
  // Each runner publishes { userId, name, avatar, handle, distanceMeters,
  // elapsedSec, finished, updatedAt } on the shared channel.
  // We keep a Map<userId, payload> for aggregation.
  const [peers, setPeers] = useState(() => new Map());
  const channelRef = useRef(null);

  useEffect(() => {
    if (!supabase || !id || !user?.id) return;
    const channel = supabase.channel(`race:${id}`, {
      config: { broadcast: { self: true }, presence: { key: user.id } },
    });
    channelRef.current = channel;
    channel.on('broadcast', { event: 'progress' }, ({ payload }) => {
      if (!payload?.userId) return;
      setPeers((prev) => {
        const next = new Map(prev);
        next.set(payload.userId, payload);
        return next;
      });
    });
    channel.subscribe();
    return () => {
      try { channel.unsubscribe(); } catch { /* noop */ }
      channelRef.current = null;
    };
  }, [id, user?.id]);

  // Publish OUR progress every BROADCAST_INTERVAL_MS while the race is live.
  const finishedRef = useRef(false);
  useEffect(() => {
    if (!running || !channelRef.current || !user?.id) return;
    const send = () => {
      channelRef.current?.send({
        type: 'broadcast',
        event: 'progress',
        payload: {
          userId: user.id,
          name: profile?.name || 'Runner',
          avatar: profile?.avatar_url || null,
          handle: profile?.handle || '',
          distanceMeters: gps.distanceMeters,
          elapsedSec: elapsed,
          finished: finishedRef.current,
          updatedAt: Date.now(),
        },
      });
    };
    // Push immediately, then on interval.
    send();
    const iv = setInterval(send, BROADCAST_INTERVAL_MS);
    return () => clearInterval(iv);
  }, [running, gps.distanceMeters, elapsed, user?.id, profile?.name, profile?.avatar_url, profile?.handle]);

  // ---- Finish flow ----------------------------------------------------
  const [busy, setBusy] = useState(false);
  // Auto-finish grace state: while active we count down 3s in a modal
  // and let the runner cancel it (false-positive geofence hit, e.g.).
  const [autoFinish, setAutoFinish] = useState(null); // { reason, at }
  const finishCommittedRef = useRef(false);

  async function submitFinish() {
    if (finishCommittedRef.current) return;
    finishCommittedRef.current = true;
    setBusy(true);
    finishedRef.current = true;
    try {
      const trackPath = gps.path.length > 0 ? gps.path : null;
      const finalCoverage = coverage ?? 95;
      await api.finishRace(id, elapsed, trackPath, finalCoverage);
      channelRef.current?.send({
        type: 'broadcast',
        event: 'progress',
        payload: {
          userId: user.id,
          name: profile?.name || 'Runner',
          avatar: profile?.avatar_url || null,
          handle: profile?.handle || '',
          distanceMeters: gps.distanceMeters,
          elapsedSec: elapsed,
          finished: true,
          updatedAt: Date.now(),
        },
      });
      nav(`/events/${id}/finish`);
    } catch (err) {
      toast.error(err.message || 'Could not submit finish');
      finishCommittedRef.current = false;
      finishedRef.current = false;
      setBusy(false);
    }
  }

  function handleFinish() {
    // Manual tap = confirmed, no grace period.
    submitFinish();
  }

  // Trigger the auto-finish grace period from either virtual-target or
  // physical-finish-geofence.
  function triggerAutoFinish(reason) {
    if (finishCommittedRef.current || autoFinish) return;
    playFinishChime();
    vibratePattern([120, 80, 120, 80, 200]);
    setAutoFinish({ reason, at: Date.now() });
  }

  function cancelAutoFinish() {
    setAutoFinish(null);
  }

  // Auto-end watcher — runs while the race is live and the finish hasn't
  // been committed. Two triggers:
  //   • Virtual (or any race lacking a finishPoint): distance >= target.
  //   • Physical: current GPS position within FINISH_RADIUS_M of finish.
  useEffect(() => {
    if (!running || autoFinish || finishCommittedRef.current) return;
    if (!event) return;

    // Virtual races auto-finish by distance.
    const isVirtual = event.eventMode === 'virtual';
    if (isVirtual && targetKm > 0 && distanceKm >= targetKm) {
      triggerAutoFinish('target');
      return;
    }

    // Physical races auto-finish when within FINISH_RADIUS_M of the finish
    // geofence. Guard: require some minimum coverage so we don't fire the
    // moment the runner is still near the start line (which for out-and-back
    // routes may coincide with finish).
    const hasFinishPin = event.finishLat != null && event.finishLng != null;
    if (!isVirtual && hasFinishPin && currentPos) {
      const minCoverageMet = targetKm === 0 || distanceKm >= targetKm * 0.5;
      if (minCoverageMet) {
        const d = metersBetween(currentPos, { lat: event.finishLat, lng: event.finishLng });
        if (d <= FINISH_RADIUS_M) {
          triggerAutoFinish('finish-line');
        }
      }
    }
  }, [running, autoFinish, event, targetKm, distanceKm, currentPos]);

  // While the grace modal is up, count down and auto-submit at 0.
  const [graceRemaining, setGraceRemaining] = useState(AUTO_FINISH_GRACE_MS);
  useEffect(() => {
    if (!autoFinish) { setGraceRemaining(AUTO_FINISH_GRACE_MS); return; }
    const startedAt = autoFinish.at;
    const iv = setInterval(() => {
      const rem = Math.max(0, AUTO_FINISH_GRACE_MS - (Date.now() - startedAt));
      setGraceRemaining(rem);
      if (rem <= 0) {
        clearInterval(iv);
        submitFinish();
      }
    }, 100);
    return () => clearInterval(iv);
  }, [autoFinish]);

  // ---- Leaderboard rows ----------------------------------------------
  const friendIdSet = useMemo(
    () => new Set((friends || []).map((f) => f.id)),
    [friends]
  );

  // Always include SELF (even before first broadcast lands) so the "You"
  // row is present immediately.
  const selfRow = user?.id
    ? {
        userId: user.id,
        name: profile?.name || 'You',
        avatar: profile?.avatar_url || null,
        handle: profile?.handle || '',
        distanceMeters: gps.distanceMeters,
        elapsedSec: elapsed,
        finished: finishedRef.current,
        updatedAt: Date.now(),
      }
    : null;

  const rows = useMemo(() => {
    const map = new Map(peers);
    if (selfRow) map.set(selfRow.userId, selfRow);
    // Sort: finished first (by fastest time), then by distance desc.
    const arr = [...map.values()].sort((a, b) => {
      if (a.finished && !b.finished) return -1;
      if (!a.finished && b.finished) return 1;
      if (a.finished && b.finished) return (a.elapsedSec || 0) - (b.elapsedSec || 0);
      return (b.distanceMeters || 0) - (a.distanceMeters || 0);
    });
    return arr.map((r, i) => ({ ...r, rank: i + 1 }));
  }, [peers, selfRow?.distanceMeters, selfRow?.elapsedSec, selfRow?.finished]);

  const [lbTab, setLbTab] = useState('all');
  const filteredRows = useMemo(() => {
    if (lbTab === 'friends') {
      return rows.filter((r) => r.userId === user?.id || friendIdSet.has(r.userId));
    }
    return rows;
  }, [rows, lbTab, user?.id, friendIdSet]);

  // ---- Render ---------------------------------------------------------
  const hasRealPath = gps.path.length > 1;
  const displayPath = hasRealPath ? gps.path : [];
  const currentIdx = hasRealPath ? gps.path.length - 1 : 0;

  // If the race is not actually live (spectator, or came to this URL early)
  // show a graceful fallback.
  if (event && event.raceState !== 'live') {
    return (
      <div className="px-4 pt-6 pb-6 min-h-full flex flex-col items-center justify-center text-center" data-testid="race-not-live">
        <p className="rivl-label">Race not live</p>
        <h1 className="font-display text-2xl font-bold tracking-tight mt-2">
          {event.raceState === 'completed' ? 'This race has ended' : 'Waiting for the organizer to start the race'}
        </h1>
        <Button className="mt-6" onClick={() => nav(`/events/${id}`)}>Back to event</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground relative" data-testid="live-race">
      <div className="px-4 pt-4 pb-3 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <p className="rivl-label">Race live</p>
            <h2 className="font-display text-base font-semibold leading-tight truncate">{event?.name}</h2>
          </div>
          <GpsBadge gps={gps} />
        </div>
        {gps.error && (
          <p className="text-[11px] text-destructive mt-2" data-testid="gps-error">
            GPS error: {gps.error}. Check location permission.
          </p>
        )}
      </div>

      <div className="flex-1 flex flex-col">
        <div className="px-4 py-5 grid grid-cols-3 gap-3">
          <Stat label="Distance" value={distanceDisplay} unit={unitLabel(units)} testid="race-distance" />
          <Stat label="Time" value={formatDuration(elapsed)} testid="race-time" />
          <Stat label="Pace" value={paceLabel} unit={`/${unitLabel(units)}`} testid="race-pace" />
        </div>

        {coverage !== null && (
          <div className="px-4" data-testid="race-progress">
            <div className="h-1.5 w-full bg-secondary rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: `${coverage}%` }}
              />
            </div>
            <div className="flex justify-between text-[10px] text-muted-foreground mt-1 font-mono">
              <span>{distanceKm.toFixed(2)} km</span>
              <span>Target {targetKm} km · {coverage}%</span>
            </div>
          </div>
        )}

        <div className="px-4 mt-4">
          <MapView
            height={220}
            path={displayPath}
            showCurrent={hasRealPath}
            currentIndex={currentIdx}
            showUserLocation
            userAvatarUrl={profile?.avatar_url}
            startPoint={event?.startLat != null && event?.startLng != null ? { lat: event.startLat, lng: event.startLng } : null}
            finishPoint={event?.finishLat != null && event?.finishLng != null ? { lat: event.finishLat, lng: event.finishLng } : null}
            checkpoints={eventCheckpoints}
            passedCheckpointIndices={passedIndices}
            followPosition={running && currentPos ? currentPos : null}
          />
        </div>

        {/* Checkpoint chip row — shows a green dot per passed checkpoint */}
        {eventCheckpoints.length > 0 && (
          <div className="px-4 mt-3" data-testid="race-checkpoints-row">
            <div className="rivl-label mb-2">
              Checkpoints · {passedIndices.size}/{eventCheckpoints.length}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {eventCheckpoints.map((cp, i) => {
                const passed = passedIndices.has(i);
                return (
                  <div
                    key={`cp-chip-${i}`}
                    data-testid={`race-checkpoint-chip-${i}`}
                    data-passed={passed ? 'true' : 'false'}
                    className={`px-2 py-1 rounded-full text-[10px] font-mono font-bold border transition-colors ${
                      passed
                        ? 'bg-success/10 text-success border-success/40'
                        : 'bg-secondary text-muted-foreground border-border'
                    }`}
                  >
                    {passed ? '✓' : '○'} {i + 1}{cp?.name ? ` · ${cp.name}` : ''}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ---- Leaderboard ---- */}
        <div className="px-4 mt-5" data-testid="race-leaderboard">
          <div className="flex items-center justify-between mb-2">
            <h3 className="rivl-label">Live leaderboard</h3>
            <div className="inline-flex items-center rounded-md border border-border p-0.5 text-[11px] font-semibold" data-testid="race-leaderboard-tabs">
              <button
                onClick={() => setLbTab('all')}
                data-testid="race-lb-tab-all"
                className={`px-2.5 py-1 rounded-sm flex items-center gap-1 ${lbTab === 'all' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
              >
                <Users className="h-3 w-3" /> All
              </button>
              <button
                onClick={() => setLbTab('friends')}
                data-testid="race-lb-tab-friends"
                className={`px-2.5 py-1 rounded-sm flex items-center gap-1 ${lbTab === 'friends' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
              >
                <User className="h-3 w-3" /> Friends
              </button>
            </div>
          </div>
          <div className="rivl-card divide-y divide-border max-h-64 overflow-auto">
            {filteredRows.length === 0 ? (
              <div className="py-4 text-center text-xs text-muted-foreground">
                {lbTab === 'friends' ? 'No friends in this race yet.' : 'Waiting for other runners…'}
              </div>
            ) : (
              filteredRows.map((r) => (
                <LeaderRow
                  key={r.userId}
                  row={r}
                  isYou={r.userId === user?.id}
                  units={units}
                />
              ))
            )}
          </div>
        </div>

        <div className="px-4 py-4 mt-2">
          <Button
            onClick={handleFinish}
            disabled={busy || !running}
            data-testid="race-finish-btn"
            className="w-full h-14 rounded-md flex items-center gap-2 text-base font-semibold bg-foreground text-background hover:bg-foreground/90"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Square className="h-5 w-5" /> Finish</>}
          </Button>
        </div>
      </div>

      {/* ---- 3-2-1 overlay (synced) ---- */}
      {showCountdown && (
        <div
          className="absolute inset-0 z-50 bg-background/95 backdrop-blur-sm flex flex-col items-center justify-center"
          data-testid="race-countdown"
        >
          <div className="rivl-label text-[10px] mb-3">Race starting</div>
          <div
            key={countdown}
            className="font-display text-[140px] font-black leading-none text-primary tabular-nums animate-pulse"
          >
            {countdown}
          </div>
          <div className="mt-4 text-xs text-muted-foreground">
            {event?.eventMode === 'virtual' ? 'Everyone in the race is starting now' : 'Get on the start line'}
          </div>
        </div>
      )}

      {/* ---- Auto-finish grace modal ---- */}
      {autoFinish && (
        <div
          className="absolute inset-0 z-50 bg-background/95 backdrop-blur-sm flex items-center justify-center p-6"
          data-testid="race-auto-finish"
        >
          <div className="rivl-card p-6 w-full max-w-xs text-center">
            <div className="mx-auto h-14 w-14 rounded-md bg-success/10 text-success flex items-center justify-center">
              <Flag className="h-7 w-7" />
            </div>
            <p className="rivl-label mt-3">
              {autoFinish.reason === 'target' ? 'Target reached' : 'Finish line reached'}
            </p>
            <h2 className="font-display text-2xl font-bold tracking-tight mt-1">Finishing…</h2>
            <p className="text-xs text-muted-foreground mt-2">
              {autoFinish.reason === 'target'
                ? `You've hit ${targetKm} km. Locking your time in ${Math.ceil(graceRemaining / 1000)}s.`
                : `You're within ${FINISH_RADIUS_M} m of the finish. Locking your time in ${Math.ceil(graceRemaining / 1000)}s.`}
            </p>
            <div
              className="mt-4 h-1.5 w-full bg-secondary rounded-full overflow-hidden"
              data-testid="race-auto-finish-progress"
            >
              <div
                className="h-full bg-success rounded-full"
                style={{ width: `${((AUTO_FINISH_GRACE_MS - graceRemaining) / AUTO_FINISH_GRACE_MS) * 100}%`, transition: 'width 100ms linear' }}
              />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                onClick={cancelAutoFinish}
                data-testid="race-auto-finish-cancel"
                className="h-11 rounded-md"
              >
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
              <Button
                onClick={submitFinish}
                data-testid="race-auto-finish-now"
                className="h-11 rounded-md bg-foreground text-background hover:bg-foreground/90"
              >
                Finish now
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GpsBadge({ gps }) {
  if (!gps.supported || gps.error) {
    return (
      <div className="flex items-center gap-1 text-[10px] text-destructive font-mono">
        <WifiOff className="h-3 w-3" /> GPS OFF
      </div>
    );
  }
  if (gps.accuracy == null) {
    return (
      <div className="flex items-center gap-1 text-[10px] text-muted-foreground font-mono">
        <Wifi className="h-3 w-3 animate-pulse" /> ACQUIRING
      </div>
    );
  }
  const cls = gps.accuracy < 15 ? 'text-success' : gps.accuracy < 40 ? 'text-primary' : 'text-destructive';
  return (
    <div className={`flex items-center gap-1 text-[10px] font-mono ${cls}`}>
      <Wifi className="h-3 w-3" /> ±{Math.round(gps.accuracy)}m
    </div>
  );
}

function LeaderRow({ row, isYou, units }) {
  const km = (row.distanceMeters || 0) / 1000;
  const distDisplay = kmToDisplay(km, units).toFixed(2);
  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 ${isYou ? 'bg-primary/5' : ''}`}
      data-testid={`race-lb-row-${row.userId}`}
    >
      <div className="font-mono font-bold w-6 text-center text-primary text-sm">#{row.rank}</div>
      {row.avatar ? (
        <img src={row.avatar} alt="" className="h-8 w-8 rounded-full object-cover" />
      ) : (
        <div className="h-8 w-8 rounded-full bg-secondary text-foreground flex items-center justify-center text-xs font-bold uppercase">
          {(row.name || '?').charAt(0)}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate flex items-center gap-2">
          {row.name}
          {isYou && <span className="rivl-label text-[9px] text-primary">You</span>}
        </div>
        <div className="text-[11px] font-mono text-muted-foreground truncate">
          {row.finished ? (
            <>Finished · {formatDuration(row.elapsedSec || 0)}</>
          ) : (
            <>{distDisplay} {unitLabel(units)} · {formatDuration(row.elapsedSec || 0)}</>
          )}
        </div>
      </div>
      {row.finished && <div className="text-[10px] uppercase tracking-widest text-success font-bold">Done</div>}
    </div>
  );
}

function Stat({ label, value, unit, testid }) {
  return (
    <div className="text-center" data-testid={testid}>
      <div className="rivl-label text-[10px]">{label}</div>
      <div className="font-mono font-bold text-3xl tracking-tight tabular-nums mt-1">{value}</div>
      {unit && <div className="rivl-label text-[9px]">{unit}</div>}
    </div>
  );
}
